import { Component, OnInit } from '@angular/core';
import { AngularCsv } from 'angular-csv-ext/dist/Angular-csv';
import { AssociationMongo } from 'app/models/association';
import { ApplicationType } from 'app/models/application';
import { ApplicationsService } from 'app/services/applications.service';
import { AssociationsService } from 'app/services/associations.service';
import { Alert } from 'app/shared/alerts/simple-message/simple-message.component';
import { SessionStorage } from 'app/storage/session.storage';
import { SopStorage } from 'app/storage/sop.storage';
import { catchError, EMPTY, forkJoin, of } from 'rxjs';
import * as _ from 'lodash';
import { AssociationHelper } from 'app/storage/association.storage';
import { Router } from '@angular/router';
import { schemaColumns } from 'app/form/meeting-form/schema-columns';

const SERVICE_TYPES: ApplicationType[] = ['service1', 'service2', 'service3'];

// PSS report CSV columns follow pss_report.json
const PSS_REPORT_COLUMNS = schemaColumns('pss_report')


@Component({
  selector: 'services',
  templateUrl: './services.component.html',
  styleUrls: ['../sop.component.scss', './services.component.scss']
})
export class ServicesComponent implements OnInit {
  loading: boolean = true
  sortSelected = 'Project'
  sortDirection = 'ASC'
  headerRow = [ 
    'ID',
    'Project', 
    'Submission date UTC',
    'EPE', 
    'Status', 
    'Approv date UTC',
    'Meeting 1 set/done',
    'Meeting 1 date',
    'Meeting 2 set/done',
    'Meeting 2 date',
    'Meeting 3 set/done',
    'Meeting 3 date',
    'Advice',
    'Final meeting set/done',
    'Final meeting date',
    'Report Submission',
    'Service complete'
  ]

  reportHeaderRow = [
    'ID',
    'Project',
    'EPE',
    'Topics',
    'Status',
    '1) Instructions to fill in this report',
    '2.1) Project description',
    '2.2) Description of expectations and meetings',
    '3.1) Standardisation readiness',
    '3.2) Summary of the Standardisation Orientation Tool advice',
    '3.2.1) Standards and standardisation mapping landscape',
    '3.2.1) Standards and standardisation mapping landscape - Other',
    '3.2.2) What results have already been achieved or are expected to be achieved in the next few months by the project based on the delivered activities?',
    '3.3) Access to standards',
    '3.3) Access to standards - Other',
    '3.4) Standardisation strategy and engagement',
    '3.4) Standardisation strategy and engagement - Other',
    '3.5) Standards deliverables',
    '3.5) Standards deliverables - Other',
    '3.6) Training material',
    '3.6) Training material - Other',
    '3.6.1) Did the service recipient complete any of the following (tick what appropriate)?',
    '3.6.2) If applicable, please specify any other training material suggested to the project in the box below',
    '4) Assessment of standardisation potential',
    '4.1) Indicator 1: Necessity',
    '4.2) Indicator 2: Transferability',
    '4.3) Indicator 3: Feasibility',
    '4.4) Indicator 4: Complementation of standardization landscape',
    '4.5) Indicator 5: Further input needed',
    '5) Final Recommendations',
    '5.1) What is the recommended strategy and next steps for the project?',
    '5.2) Would you recommend that the project pursues the accelerator service?',
    '6) Closure',
    '6.1) Has the project completed the satisfaction survey?',
    '6.2) Has the project booked a slot for follow up interview?'
  ]

  pssReportHeaderRow = [
    'ID',
    'Project',
    'EPE',
    'Topics',
    'Status',
    ...PSS_REPORT_COLUMNS
  ]

  adviceHeaderRow = [ 
    'ID',
    'Project', 
    'EPE', 
    'Status', 
    'Advice'
  ]

  epes: any[] | null = []
  associations: any[] | null = []
  reports: any[] | null = []
  pssReports: any[] | null = []
  advice: any[] | null = []
  projects: any[] | null = []

  showAlert: boolean = false
  alertMessage: string = ''
  alertType: Alert = Alert.SUCCESS


  constructor(
    private applicationsService: ApplicationsService,
    private associationService: AssociationsService,
    private cache: SopStorage,
    private auth: SessionStorage,
    private helper: AssociationHelper,
    private router: Router
  ) { }

  ngOnInit(): void {
    const cachedProjects = this.cache.getProjects()
    const cachedEpes = this.cache.getEpes()

    forkJoin({
      projects: !cachedProjects ? this.applicationsService.getAll({ include_user: true }) : of(null),
      epes: !cachedEpes ? this.applicationsService.getAll({ type: 'epe', include_user: true }) : of(null),
      associations: this.associationService.getAllServices()
    })
    .pipe(
      catchError(
        err => {
          this.addAlert("Association retriving error", Alert.ERROR)
          return EMPTY
        }
      )
    )
    .subscribe(
      then => {
        this.projects = then.projects
          ? then.projects
              .filter((a: any) => SERVICE_TYPES.includes(a.application_type))
              .map((app: any) => ({
                ...(app.form_data || {}),
                ...(app.user_info || {}),
                ...app,
                id: app.user_id ?? app.id,
                application_id: app._id?.['$oid'] ?? app._id,
                sop_status: app.status,
              }))
          : cachedProjects
        this.epes = then.epes
          ? then.epes.map((app: any) => ({
              ...(app.form_data || {}),
              ...(app.user_info || {}),
              ...app,
              id: app.user_id ?? app.id,
              application_id: app._id?.['$oid'] ?? app._id,
              sop_status: app.status,
            }))
          : cachedEpes
        this.associations = then.associations?.map(
          association => {
            const pid = association.project_application_id ?? association.project_id
            const eid = association.epe_application_id ?? association.epe_id
            const project = this.projects?.find(p => p.application_id == pid) ?? this.projects?.find(p => p.id == pid)
            const epe = this.epes?.find(e => e.application_id == eid) ?? this.epes?.find(e => e.id == eid)
            return {
              ...association,
              project_id: pid,
              epe_id: eid,
              'ID': pid+"_"+eid,
              'Project': project?.project_acronym,
              'Submission date UTC': this.toDate(project?.created_at),
              'EPE': (epe?.name ?? '') + " " + (epe?.surname ?? ''),
              'Status': association.association_status,
              'Approv date UTC': association.approvation_date ? association.approvation_date : '',
              'Meeting 1 set/done': this.getMeetingStatus(association.meeting_form_1),
              'Meeting 1 date': this.getMeetingDate(association.meeting_form_1),
              'Meeting 2 set/done': this.getMeetingStatus(association.meeting_form_2),
              'Meeting 2 date': this.getMeetingDate(association.meeting_form_2),
              'Meeting 3 set/done': this.getMeetingStatus(association.meeting_form_3),
              'Meeting 3 date': this.getMeetingDate(association.meeting_form_3),
              'Advice':  this.getMeetingComplete(association.meeting_form_4),
              'Final meeting set/done': this.getMeetingStatus(association.meeting_form_5),
              'Final meeting date': this.getMeetingDate(association.meeting_form_5),
              'Report Submission': this.getSubmissionDate(association.report_submission_date, association.meeting_form_5),
              'Service complete': this.getMeetingComplete(association.meeting_form_5),
            }
          }
        )

        // Mentoring report export (service1, plus legacy associations); PSS has its own schema/export
        this.reports = then.associations?.filter(association => !this.isPss(association)).map(
          association => {
            const pid = association.project_application_id ?? association.project_id
            const eid = association.epe_application_id ?? association.epe_id
            const project = this.projects?.find(p => p.application_id == pid) ?? this.projects?.find(p => p.id == pid)
            const epe = this.epes?.find(e => e.application_id == eid) ?? this.epes?.find(e => e.id == eid)
            return {
            'ID': pid+"_"+eid,
            'Project': project?.project_acronym,
            'EPE': (epe?.name ?? '') + " " + (epe?.surname ?? ''),
            'Topics': this.generateTopics(project),
            'Status': association.association_status,
            '1) Instructions to fill in this report' : association.report && association.report['1) Instructions to fill in this report']? association.report['1) Instructions to fill in this report'] : "",
            '2.1) Project description' : association.report && association.report['2.1) Project description'] ? association.report['2.1) Project description'] : "",
            '2.2) Description of expectations and meetings' : association.report && association.report['2.2) Description of expectations and meetings'] ? association.report['2.2) Description of expectations and meetings'] : "",
            '3.1) Standardisation readiness' : association.report && association.report['3.1) Standardisation readiness'] ? association.report['3.1) Standardisation readiness'] : "",
            '3.2) Summary of the Standardisation Orientation Tool advice' : association.report && association.report['3.2) Summary of the Standardisation Orientation Tool advice'] ? association.report['3.2) Summary of the Standardisation Orientation Tool advice'] : "",
            '3.2.1) Standards and standardisation mapping landscape' : association.report && association.report['3.2.1) Standards and standardisation mapping landscape']? association.report['3.2.1) Standards and standardisation mapping landscape']?.join("\n "): "",
            '3.2.1) Standards and standardisation mapping landscape - Other' : association.report && association.report['3.2.1) Standards and standardisation mapping landscape - Other'] ? association.report['3.2.1) Standards and standardisation mapping landscape - Other'] : "",
            '3.2.2) What results have already been achieved or are expected to be achieved in the next few months by the project based on the delivered activities?' : association.report && association.report['3.2.2) What results have already been achieved or are expected to be achieved in the next few months by the project based on the delivered activities?'] ? association.report['3.2.2) What results have already been achieved or are expected to be achieved in the next few months by the project based on the delivered activities?'] : "",
            '3.3) Access to standards' : association.report && association.report['3.3) Access to standards'] ? association.report['3.3) Access to standards']?.join("\n ") : "",
            '3.3) Access to standards - Other' : association.report && association.report['3.3) Access to standards - Other'] ? association.report['3.3) Access to standards - Other'] : "",
            '3.4) Standardisation strategy and engagement' : association.report && association.report['3.4) Standardisation strategy and engagement'] ? association.report['3.4) Standardisation strategy and engagement']?.join("\n "): "",
            '3.4) Standardisation strategy and engagement - Other' : association.report && association.report['3.4) Standardisation strategy and engagement - Other'] ? association.report['3.4) Standardisation strategy and engagement - Other'] : "",
            '3.5) Standards deliverables' : association.report && association.report['3.5) Standards deliverables'] ? association.report['3.5) Standards deliverables']?.join("\n ") : "",
            '3.5) Standards deliverables - Other' : association.report && association.report['3.5) Standards deliverables - Other'] ? association.report['3.5) Standards deliverables - Other'] : "",
            '3.6) Training material' : association.report && association.report['3.6) Training material'] ? association.report['3.6) Training material']?.join("\n ") : "",
            '3.6) Training material - Other' : association.report && association.report['3.6) Training material - Other'] ? association.report['3.6) Training material - Other'] : "",
            '3.6.1) Did the service recipient complete any of the following (tick what appropriate)?' : association.report && association.report['3.6.1) Did the service recipient complete any of the following (tick what appropriate)?'] ? association.report['3.6.1) Did the service recipient complete any of the following (tick what appropriate)?']?.join("\n ") : "",
            '3.6.2) If applicable, please specify any other training material suggested to the project in the box below' : association.report && association.report['3.6.2) If applicable, please specify any other training material suggested to the project in the box below'] ? association.report['3.6.2) If applicable, please specify any other training material suggested to the project in the box below']: "",
            '4) Assessment of standardisation potential' : association.report && association.report['4) Assessment of standardisation potential'] ? association.report['4) Assessment of standardisation potential'] : "",
            '4.1) Indicator 1: Necessity' : association.report && association.report['4.1) Indicator 1: Necessity'] ? association.report['4.1) Indicator 1: Necessity'] : "",
            '4.2) Indicator 2: Transferability' : association.report && association.report['4.2) Indicator 2: Transferability'] ? association.report['4.2) Indicator 2: Transferability'] : "",
            '4.3) Indicator 3: Feasibility' : association.report && association.report['4.3) Indicator 3: Feasibility'] ? association.report['4.3) Indicator 3: Feasibility'] : "",
            '4.4) Indicator 4: Complementation of standardization landscape' : association.report && association.report['4.4) Indicator 4: Complementation of standardization landscape'] ? association.report['4.4) Indicator 4: Complementation of standardization landscape'] : "",
            '4.5) Indicator 5: Further input needed' : association.report && association.report['4.5) Indicator 5: Further input needed'] ? association.report['4.5) Indicator 5: Further input needed'] : "",
            '5) Final Recommendations' : association.report && association.report['5) Final Recommendations'] ? association.report['5) Final Recommendations'] : "",
            '5.1) What is the recommended strategy and next steps for the project?' : association.report && association.report['5.1) What is the recommended strategy and next steps for the project?'] ? association.report['5.1) What is the recommended strategy and next steps for the project?'] : "",
            '5.2) Would you recommend that the project pursues the accelerator service?' : association.report && association.report['5.2) Would you recommend that the project pursues the accelerator service?'] ? association.report['5.2) Would you recommend that the project pursues the accelerator service?'] : "",
            '6) Closure' : association.report && association.report['6) Closure'] ? association.report['6) Closure'] : "",
            '6.1) Has the project completed the satisfaction survey?' : association.report && association.report['6.1) Has the project completed the satisfaction survey?'] ? association.report['6.1) Has the project completed the satisfaction survey?'] : "",
            '6.2) Has the project booked a slot for follow up interview?' : association.report && association.report['6.2) Has the project booked a slot for follow up interview?'] ? association.report['6.2) Has the project booked a slot for follow up interview?'] : ""
          }
          }
        )
        this.pssReports = then.associations?.filter(association => this.isPss(association)).map(
          association => {
            const pid = association.project_application_id ?? association.project_id
            const eid = association.epe_application_id ?? association.epe_id
            const project = this.findProject(association)
            const epe = this.epes?.find(e => e.application_id == eid) ?? this.epes?.find(e => e.id == eid)
            // every column must be present, in header order, for the CSV rows to line up
            const row: any = {
              'ID': pid+"_"+eid,
              'Project': project?.project_acronym,
              'EPE': (epe?.name ?? '') + " " + (epe?.surname ?? ''),
              'Topics': this.generateTopics(project),
              'Status': association.association_status,
            }
            for (const column of PSS_REPORT_COLUMNS)
              row[column] = association.report?.[column] ?? ""
            return row
          }
        )
        this.advice = then.associations?.map(
          association => {
            const pid = association.project_application_id ?? association.project_id
            const eid = association.epe_application_id ?? association.epe_id
            const project = this.projects?.find(p => p.application_id == pid) ?? this.projects?.find(p => p.id == pid)
            const epe = this.epes?.find(e => e.application_id == eid) ?? this.epes?.find(e => e.id == eid)
            return {
              'ID': pid+"_"+eid,
              'Project': project?.project_acronym,
              'EPE': (epe?.name ?? '') + " " + (epe?.surname ?? ''),
              'Status': association.association_status,
              'Advice' : association.meeting_form_4 && association.meeting_form_4['Advice'] ? association.meeting_form_4['Advice'] : ""
            }
          }
        )
        this.cache.setEpes(this.epes!)
        this.cache.setProjects(this.projects!)
        this.associations?.sort(this.statusSort(this.sortDirection, this.sortSelected))
        this.loading = false
      }
    )

  }

  private findProject(association: any): any {
    const pid = association.project_application_id ?? association.project_id
    return this.projects?.find(p => p.application_id == pid) ?? this.projects?.find(p => p.id == pid)
  }

  private isPss(association: any): boolean {
    return this.findProject(association)?.application_type === 'service2'
  }

  generateTopics(project: any | undefined){
    if(!project || !project['topics']){
      return ''
    }
    const topicsField = project['topics']
    if (Array.isArray(topicsField)) {
      return topicsField.join('\n')
    }
    var topics = '';
    for (const [firstLevelKey, firstLevelValue] of Object.entries(topicsField as Record<string, any>)) {
      topics += firstLevelKey + '\n';
      for (const [secondLevelKey, secondLevelValue] of Object.entries(firstLevelValue as Record<string, any>)) {
        topics += '\t' + secondLevelKey + '\n';
        (secondLevelValue as string[]).forEach((thirdLevelKey: string) => {
          topics += '\t\t' + thirdLevelKey + '\n';
        });
      }
    }
    return topics
  }

  statusSort(sortDirection: string, sortSelected: string){
    return function(a: AssociationMongo, b: AssociationMongo) {
      if(a && b){
        if(sortDirection == 'ASC'){
          return a[sortSelected].localeCompare(b[sortSelected])
        }
        else{
          return a[sortSelected].localeCompare(b[sortSelected])*-1
        }
      }
      return 0;
    }
  }

  sortCell(cell: string){
    if(this.sortDirection == 'ASC'){
      this.sortDirection = 'DESC'
    }
    else{
      this.sortDirection = 'ASC'
    }
    this.sortSelected = cell
    console.log(this.sortDirection)
    console.log(this.sortSelected)
    this.associations?.sort(this.statusSort(this.sortDirection, this.sortSelected))
    console.log(this.associations)
  }

  exportAuthorized(){
    return this.auth.isAdmin() || this.auth.isCallManager() || this.auth.isProjectBoard()
  }

  toDate(value: string | undefined){
    if(!value)
      return ''

    const date = /^\d+$/.test(value)
      ? new Date(parseInt(value) * 1000)
      : new Date(value);

    if (isNaN(date.getTime())) return ''

    const day = ('0' + date.getDate()).slice(-2);
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const year = date.getFullYear();

    const hours = ('0' + date.getHours()).slice(-2);
    const minutes = ('0' + date.getMinutes()).slice(-2);
    const seconds = ('0' + date.getSeconds()).slice(-2);

    return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
  }

  stripHtmlTags(content: string): string {
    const div = document.createElement('div');
    div.innerHTML = content;
    return div.textContent || div.innerText || '';
  }
  
  exportReportsCSV() {
    const reports = [...this.reports!];
    const reports_picked = reports.map(rep => this.pickReportFields(rep, this.reportHeaderRow));
    const csvOptions = this.getCsvOptions('SOP Services Reports', this.reportHeaderRow);
  
    new AngularCsv(reports_picked, 'SOP-Services-Reports-HSBooster', csvOptions);
  }
  
  exportPssReportsCSV() {
    const reports = [...this.pssReports!];
    const reports_picked = reports.map(rep => this.pickReportFields(rep, this.pssReportHeaderRow));
    const csvOptions = this.getCsvOptions('SOP PSS Reports', this.pssReportHeaderRow);

    new AngularCsv(reports_picked, 'SOP-PSS-Reports-HSBooster', csvOptions);
  }

  exportAdvicesCSV() {
    const advice = [...this.advice!];
    const advice_picked = advice.map(rep => this.pickReportFields(rep, this.adviceHeaderRow));
    const csvOptions = this.getCsvOptions('SOP Services Advice', this.adviceHeaderRow);
  
    new AngularCsv(advice_picked, 'SOP-Services-Advice-HSBooster', csvOptions);
  }
  
  exportCSV() {
    const associations = [...this.associations!];
    const associations_picked = associations.map(ass => _.pick(ass, this.headerRow));
    const csvOptions = this.getCsvOptions('SOP Services', this.headerRow);
  
    new AngularCsv(associations_picked, 'SOP-Services-HSBooster', csvOptions);
  }
  
  pickReportFields(report: any, headerRow: string[]) {
    const pickedReport = _.pick(report, headerRow);
  
    Object.keys(pickedReport).forEach(field => {
      pickedReport[field] = this.stripHtmlTags(pickedReport[field]);
    });
  
    return pickedReport;
  }
  
  getCsvOptions(title: string, headerRow: string[]) {
    return {
      fieldSeparator: ';',
      quoteStrings: '"',
      decimalseparator: '.',
      showLabels: true,
      showTitle: true,
      title: title,
      useBom: true,
      noDownload: false,
      headers: headerRow
    };
  }
  

  openNewTab(url: any){
    console.log(url, "provaURL")
    window.open(url)
  }

  getSubmissionDate(report_submission_date: string, meeting_form_5: any){
    console.log(report_submission_date, meeting_form_5)
    if(!meeting_form_5){
      return ''
    }
    else if(report_submission_date){
      return report_submission_date
    }
    else if(meeting_form_5['complete']){
      return meeting_form_5['date']
    }
    else{
      return ''
    }
  }

  getMeetingComplete(meetingForm: any){
    if(!meetingForm){
      return ''
    }
    if(meetingForm['complete']){
      return 'DONE'
    }

    return ''
  }

  getMeetingStatus(meetingForm: any){
    if(!meetingForm){
      return ''
    }
    if(meetingForm['dateSetted']){
      if(meetingForm['meetingCompleted']){
        return 'DONE'
      }
      else{
        return 'SET'
      }
    }
    return ''
  }

  getMeetingDate(meetingForm: any){
    if(!meetingForm){
      return ''
    }
    if(meetingForm['dateSetted'] && meetingForm['date']){
      return meetingForm['date']
    }
    return ''
  }

  addAlert(message: string, type: Alert){
    this.alertMessage = message
    this.alertType = type
    this.showAlert = true
  }

  isCurrentEpe(status: string | undefined): boolean{

    switch(status){
      case 'open':
      case 'waiting':
      case 'not_approved':
      case undefined:
        return false
    }
    return true

  }

  goToService(project: string | undefined, epe: string | undefined){
    if(!project || !epe)
      return

    let projectAssociations = this.associations?.filter( x => x.project_id == project)

    let epe_id = projectAssociations!.find(x => this.isCurrentEpe(x.association_status))?.epe_id

    if(epe_id)
      this.helper.setSelectedEpe(epe_id)
    else
      this.helper.setSelectedEpe('')

    let meeting = projectAssociations!.find( x => x.epe_id == epe_id && x.project_id == project)?.meeting

    if(meeting)
      this.helper.setMeetingForm(meeting)

    this.helper.setSelectedProject(project)

    let form = this.helper.getMeetingForm()
    if(this.auth.isProject() && form <= 1){
      this.addAlert("Please wait until the Epe complete at least one form", Alert.WARNING)
      return
    }
    if(form === 0 || !form)
      form = 1
    form = this.auth.isProject() && form < 7 ? form - 1 : form
    console.log(form)
    let route = this.auth.isProject() ? 'summary' : 'first'
    if(form < 6){
      console.log("There")
      this.router.navigateByUrl(`form/(meeting-form:meeting_${form}/(meeting_${form}:${route}))`)
    }
    else if(this.auth.isProjectBoard() || this.auth.isCallManager() || this.auth.isAdmin()){
      this.router.navigateByUrl(`form/(meeting-form:survey-form/(survey-form:admin))`)
    }
    else if(form == 6){
      this.router.navigateByUrl(`form/(meeting-form:survey-form)`)
    }
    else{
      console.log("here")
      this.router.navigateByUrl(`form/(meeting-form:survey-form/(survey-form:summary))`)
    }

  }

}
